import {useTranslation} from "react-i18next";
import PageBackground from "../components/PageBackground";
import {Button, TextLinkButton} from "../components/Button";
import SectionTitle from "../components/SectionTitle";
import PageSection from "../components/PageSection";
import Quote from "../components/Quote";
import PartnerLogos from "../components/PartnerLogos";
import HeroContent from "../components/HeroContent";
import {
    DocsHomeUrl, DocsSmartContractsUrl,
    DocsThetaVideoAPIUrl, DocsTNT20TutorialUrl, GithubUrl, WhitepapersMainnet4Url,
} from "../constants";

import devsBackground from '../assets/backgrounds/devs.jpg';
import quoteLogosReplay from '../assets/logos/quotes/replay@2x.png';
import quoteLogosOpenTheta from "../assets/logos/quotes/opentheta@2x.png";
import quoteLogosPassaways from "../assets/logos/quotes/passaways@2x.png";
import mn4Image from '../assets/images/metachain/mn4@2x.png';

import pLogosLine from '../assets/logos/developer-partners/line@2x.png';
import pLogosKlaytn from '../assets/logos/developer-partners/klaytn@2x.png';
import pLogosChainlink from '../assets/logos/developer-partners/chainlink@2x.png';
import pLogosReplay from '../assets/logos/developer-partners/replay@2x.png';
import pLogosMetapass from '../assets/logos/developer-partners/metapass@2x.png';
import pLogosOpentheta from '../assets/logos/developer-partners/opentheta@2x.png';
import pLogosPassaways from '../assets/logos/developer-partners/passaways@2x.png';
import pLogosDecentraland from '../assets/logos/developer-partners/decentraland@2x.png';
import pLogosUltra from '../assets/logos/developer-partners/ultra@2x.png';
import Quotes from "../components/Quotes";

const DeveloperCard = ({title, subtitle, cta}) => {
    return (
        <div className={'DeveloperCard'}>
            <div className={'DeveloperCard__title'}>
                {title}
            </div>
            <div className={'DeveloperCard__subtitle'}>
                {subtitle}
            </div>
            {
                cta &&
                <TextLinkButton className={'DeveloperCard__cta'}
                                target={(cta.target || '_blank')}
                                href={cta.href}
                >
                    {cta.title}
                </TextLinkButton>
            }
        </div>
    )
};


const DevelopersPage = () => {
    const { t } = useTranslation();

    return (
        <div className={'Page DevelopersPage'}>
            <PageSection>
                <PageBackground src={devsBackground}/>
                <HeroContent color={'purple'}
                             category={t('navbar.developers')}
                             title={t('developers.title')}
                             subtitle={t('developers.subtitle')}
                />

                <div className={'DevelopersPage__ctas'}>
                    <Button href={DocsHomeUrl}
                            target={'_blank'}
                            size={'large'}
                            color={'green'}
                            className={'MainCTAButton'}
                    >
                        {t('developers.cta-1')}
                    </Button>
                </div>

                <div className={'DeveloperCards'}
                >
                    <DeveloperCard title={t('developers.cards.smart-contract.title')}
                                   subtitle={t('developers.cards.smart-contract.subtitle')}
                                   cta={{
                                       title: t('developers.cards.smart-contract.cta-title'),
                                       href: DocsSmartContractsUrl
                                   }}
                    />
                    <DeveloperCard title={t('developers.cards.tnt20.title')}
                                   subtitle={t('developers.cards.tnt20.subtitle')}
                                   cta={{
                                       title: t('developers.cards.tnt20.cta-title'),
                                       href: DocsTNT20TutorialUrl
                                   }}
                    />
                    <DeveloperCard title={t('developers.cards.github.title')}
                                   subtitle={t('developers.cards.github.subtitle')}
                                   cta={{
                                       title: t('developers.cards.github.cta-title'),
                                       href: GithubUrl
                                   }}
                    />
                    <DeveloperCard title={t('developers.cards.ecosystem.title')}
                                   subtitle={t('developers.cards.ecosystem.subtitle')}
                                   cta={{
                                       title: t('developers.cards.ecosystem.cta-title'),
                                       href: '/ecosystem',
                                       target:'_self'
                                   }}
                    />
                </div>

                <SectionTitle title={t('developers.logos.p')}/>
                <PartnerLogos logos={[
                    pLogosLine, pLogosKlaytn, pLogosChainlink, pLogosReplay, pLogosMetapass, pLogosOpentheta, pLogosPassaways, pLogosUltra, pLogosDecentraland
                ]}/>

                <div className={'DevelopersMainnet4'}
                     id={'metachain-overview'}
                >
                    <div className={'DevelopersMainnet4__imagery'}>
                        <img src={mn4Image}/>
                    </div>
                    <div className={'DevelopersMainnet4__info'}>
                        <div className={'DevelopersMainnet4__rows'}>
                            <div className={'DevelopersMainnet4__row'}>
                                <p>
                                    The Theta Metachain is an interconnected network of blockchains, a “chain of chains”. It will allow permissionless horizontal scaling of the Theta blockchain network in order to achieve potentially unlimited transactional throughput and subsecond block finalization time. This enables a customizable, highly scalable blockchain tailored for emerging Web3 businesses in the media and entertainment space.
                                </p>
                            </div>
                            <div className={'DevelopersMainnet4__row'}>
                                <p>Subchain - horizontal scale, near instant finality</p>
                                <TextLinkButton href={WhitepapersMainnet4Url}
                                                target={'_blank'}
                                >
                                    View Documentation
                                </TextLinkButton>
                            </div>
                            <div className={'DevelopersMainnet4__row'}>
                                <p>Web3 video infrastructure to build on.</p>
                                <TextLinkButton href={DocsThetaVideoAPIUrl}
                                                target={'_blank'}>
                                    View Documentation
                                </TextLinkButton>
                            </div>
                            <div className={'DevelopersMainnet4__row'}>
                                <p>Own governance tokens, user rewards and utility - TNT20</p>
                                <TextLinkButton href={DocsTNT20TutorialUrl}
                                                target={'_blank'}>
                                    View Guide
                                </TextLinkButton>
                            </div>
                        </div>

                        <Button color={'green'}
                                size={'large'}
                                href={WhitepapersMainnet4Url}
                                target={'_blank'}
                        >
                            Mainnet 4.0 Whitepaper
                        </Button>
                    </div>
                </div>

                <Quotes>
                    <Quote body={t('quotes.replay.body')}
                           logo={quoteLogosReplay}
                    />
                    <Quote body={t('quotes.opentheta.body')}
                           logo={quoteLogosOpenTheta}
                    />
                    <Quote body={t('quotes.passaways.body')}
                           logo={quoteLogosPassaways}
                    />
                </Quotes>
            </PageSection>

        </div>
    )
};

export default DevelopersPage;